import React from 'react';
import { motion } from 'motion/react';
import { Shield, Target, Eye, Award, CheckCircle2, Globe, Quote, ArrowRight, User, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BUSINESS_NAME } from '@/src/constants';
import { Button } from '@/components/ui/button';
import SEO from '@/src/components/SEO';
import { organizationSchema } from '@/src/lib/schema';

export default function About() {
  return (
    <div className="flex flex-col">
      <SEO 
        title="About AGRANDINTL – Expert Cybersecurity Consulting in DMV"
        description="Learn about AGRANDINTL, a cybersecurity firm founded by Fabiyan Anik. We specialize in practical risk management and cloud security for businesses in DC, VA, and MD."
        keywords="about AGRANDINTL, Fabiyan Anik, cybersecurity expert DC, security consulting DMV, cloud security virginia"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "mainEntity": organizationSchema
        }}
      />
      {/* Page Header */}
      <section className="relative min-h-[40vh] flex flex-col justify-center overflow-hidden pt-32 pb-12">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em] mb-8"
            >
              The Consulting Standard
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-5xl lg:text-6xl font-display font-bold mb-8 tracking-tight leading-tight"
            >
              Our <br />
              <span className="text-accent">Foundation</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed font-normal max-w-2xl"
            >
              Agrandintl is a professional consulting firm focused on cybersecurity, cloud compliance, and secure business operations.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em]">
                  Our Story & Mission
                </div>
                <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">
                  Security. Technology. <br />
                  <span className="text-accent">Results.</span>
                </h2>
                <div className="space-y-4 text-base text-muted-foreground font-normal leading-relaxed">
                  <p>
                    Agrandintl was founded to help businesses stay secure and grow with confidence. We believe that professional security shouldn't be complicated or out of reach for growing companies.
                  </p>
                  <p>
                    Our approach is simple: we identify your risks, provide a clear roadmap to fix them, and offer ongoing support to keep your business protected.
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-8 border-t border-white/5">
                <div className="space-y-2 group">
                  <div className="w-9 h-9 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300">
                    <Target className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="text-base font-bold font-display tracking-tight group-hover:text-accent transition-colors">Our Vision</h3>
                  <p className="text-xs text-muted-foreground font-normal leading-relaxed">
                    To become the global standard for retail and cloud security intelligence, known for our ability to harden complex environments.
                  </p>
                </div>
                <div className="space-y-2 group">
                  <div className="w-9 h-9 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-background transition-all duration-300">
                    <Eye className="w-4.5 h-4.5" />
                  </div>
                  <h3 className="text-base font-bold font-display tracking-tight group-hover:text-accent transition-colors">Our Values</h3>
                  <p className="text-xs text-muted-foreground font-normal leading-relaxed">
                    Integrity, transparency, and continuous innovation. We believe in clear communication and delivering measurable improvements.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative glass-card rounded-2xl p-8 space-y-6 overflow-hidden border-white/5">
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-9 h-9 rounded-xl bg-accent/5 flex items-center justify-center text-accent">
                    <Shield className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold font-display tracking-tight">Our Expertise</h3>
                    <p className="text-accent text-[7px] font-bold uppercase tracking-[0.2em] mt-1">Strategic Security & Operations</p>
                  </div>
                </div>
                
                <div className="space-y-6 relative z-10">
                  <p className="text-sm text-muted-foreground font-normal leading-relaxed">
                    Agrandintl is led by Fabiyan Anik, a cybersecurity expert with a focus on helping businesses reduce risk and improve their security posture through practical, real-world solutions.
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="font-bold font-display text-foreground uppercase tracking-[0.1em] text-[8px]">Core Certifications</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        "CompTIA Security+", "CompTIA CySA+", "Cloud Security Expert", "Incident Response Lead"
                      ].map((cert) => (
                        <div 
                          key={cert} 
                          className="flex items-center gap-2 text-xs text-foreground/80 font-medium"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0" /> {cert}
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="text-base font-serif italic text-foreground/90 leading-relaxed border-l border-accent pl-5 py-0.5">
                    "We bring real-world incident response expertise to every engagement, helping you learn from the mistakes of others."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-secondary/[0.01] border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {[
                  { value: "100%", label: "Client Commitment" },
                  { value: "24/7", label: "Monitoring Available" },
                  { value: "150+", label: "Security Audits" },
                  { value: "Zero", label: "Technical Jargon" }
                ].map((stat, i) => (
              <div 
                key={i} 
                className="space-y-1"
              >
                <p className="text-2xl md:text-3xl font-display font-bold text-accent tracking-tight">
                  {stat.value}
                </p>
                <p className="text-[7px] text-muted-foreground uppercase tracking-[0.1em] font-bold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em]">
                Client Feedback
              </div>
              <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">
                Trusted by <br />
                <span className="text-accent">Business Owners</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  quote: "AGRANDINTL identified critical gaps in our retail POS network that our previous provider missed. Their audit was clear, professional, and gave us a direct path to remediation.",
                  author: "Retail Franchise Owner",
                  role: "Washington DC",
                  image: "https://picsum.photos/seed/retail/100/100"
                },
                {
                  quote: "The continuous monitoring gives our team peace of mind. We now have a clear view of our cloud security posture without the overhead of a full-time security hire.",
                  author: "SaaS Platform Founder",
                  role: "Virginia",
                  image: "https://picsum.photos/seed/tech/100/100"
                }
              ].map((testimonial, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card p-8 rounded-2xl border-white/5 relative group hover:border-accent/10 transition-all"
                >
                  <Quote className="absolute top-4 right-4 w-5 h-5 text-accent/5 group-hover:text-accent/10 transition-colors" />
                  <div className="space-y-4 relative z-10">
                    <p className="text-sm text-foreground/90 font-normal leading-relaxed italic">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.author} 
                        className="w-8 h-8 rounded-full border border-accent/10"
                        referrerPolicy="no-referrer"
                      />
                      <div>
                        <p className="font-bold text-xs tracking-tight">{testimonial.author}</p>
                        <p className="text-[7px] text-accent font-bold uppercase tracking-widest">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications & Professional Credibility */}
      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em]">
                Industry Validation
              </div>
              <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">
                Certified <br />
                <span className="text-accent">Excellence</span>
              </h2>
              <p className="text-base text-muted-foreground font-normal leading-relaxed max-w-xl">
                Our team maintains the highest level of industry certifications, ensuring that our methodologies are always aligned with global security standards.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "CompTIA Security+", issuer: "CompTIA" },
                  { name: "CompTIA CySA+", issuer: "CompTIA" },
                  { name: "AWS Security Specialty", issuer: "Amazon Web Services" },
                  { name: "Azure Security Engineer", issuer: "Microsoft" }
                ].map((cert, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white/[0.01] border border-white/5 space-y-1.5 group hover:border-accent/10 transition-all">
                    <p className="text-accent font-bold text-sm leading-tight">{cert.name}</p>
                    <p className="text-[8px] text-muted-foreground uppercase tracking-widest font-bold">{cert.issuer}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card rounded-2xl p-8 border-white/5 relative overflow-hidden">
              <div className="space-y-6 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent">
                    <Award className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold font-display tracking-tight">Professional Affiliations</h3>
                </div>
                <div className="space-y-3">
                  {[
                    "Information Systems Security Association (ISSA)",
                    "Cloud Security Alliance (CSA) Corporate Member",
                    "InfraGard (FBI-Private Sector Partnership)",
                    "OWASP Foundation Contributor"
                  ].map((org, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.01] border border-white/5 hover:bg-white/[0.03] transition-colors">
                      <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0" />
                      <span className="text-xs font-medium text-foreground/80">{org}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t border-white/5">
                  <p className="text-[10px] text-muted-foreground font-normal leading-relaxed italic">
                    "AGRANDINTL is committed to the continuous advancement of the cybersecurity community through active participation."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About the Founder Section */}
      <section className="py-24 relative overflow-hidden border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Left Column: Bio */}
            <div className="lg:col-span-7 space-y-10">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.2em]">
                  About the Founder
                </div>
                <div className="space-y-2">
                  <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Fabiyan Anik</h2>
                  <p className="text-accent text-xs font-bold uppercase tracking-[0.3em]">Founder, Agrandintl</p>
                </div>
                <div className="space-y-6 text-base text-muted-foreground font-normal leading-relaxed max-w-2xl">
                  <p>
                    Fabiyan Anik is a cybersecurity professional and the founder of Agrandintl, a company focused on helping businesses operate securely, reduce risk, and scale with confidence.
                  </p>
                  <p>
                    With hands-on experience in security operations, vulnerability assessment, and compliance, he works closely with businesses to identify security gaps and implement practical solutions. His approach is straightforward and results-driven—focused on protecting real business operations without unnecessary complexity.
                  </p>
                  <p>
                    Through Agrandintl, Fabiyan combines cybersecurity expertise with a broader vision of building secure, technology-driven businesses. His work spans consulting, compliance support, and the development of AI-driven solutions like Complytics.
                  </p>
                </div>
              </div>

              <div className="space-y-6 pt-8 border-t border-white/5">
                <h3 className="text-xl font-display font-bold tracking-tight">Approach</h3>
                <p className="text-base text-muted-foreground leading-relaxed font-normal max-w-2xl italic">
                  "Fabiyan believes cybersecurity should be clear, actionable, and aligned with business goals. Every engagement is designed to deliver real value—helping organizations understand their risks, prioritize what matters, and take the right steps to stay secure."
                </p>
              </div>
            </div>

            {/* Right Column: Focus & CTA */}
            <div className="lg:col-span-5 space-y-8">
              <div className="glass-card rounded-2xl p-8 border-white/5 bg-secondary/[0.01] space-y-8">
                <div className="space-y-4">
                  <h3 className="text-lg font-display font-bold tracking-tight flex items-center gap-3">
                    <Target className="w-5 h-5 text-accent" /> Core Focus
                  </h3>
                  <div className="space-y-3">
                    {[
                      "Cybersecurity risk assessment and consulting",
                      "PCI DSS and compliance support",
                      "Vulnerability identification and mitigation",
                      "Practical security strategies for growing businesses"
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.01] border border-white/5 hover:bg-white/[0.03] transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0" />
                        <span className="text-xs font-medium text-foreground/80">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-8 border-t border-white/5 space-y-6">
                  <div className="space-y-2">
                    <p className="text-sm font-bold tracking-tight">Interested in working together?</p>
                    <p className="text-xs text-muted-foreground">Schedule a direct discovery call with Fabiyan to discuss your security roadmap.</p>
                  </div>
                  <Button asChild className="w-full h-14 rounded-xl bg-accent hover:bg-accent/90 text-background font-bold text-base transition-all group">
                    <Link to="/contact">
                      Schedule a Consultation <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
